/**
 * Report Organizer
 * Transforms raw findings into actionable, prioritized report sections
 *
 * Features:
 * - Action Plan: Top 5 highest-priority fixes
 * - Quick Wins: High impact + low effort findings
 * - Above/Below Fold segmentation
 * - Device-specific grouping
 */

import type { AuditFinding, SeverityLevel, EffortEstimate } from '@/types/audit';

// ============================================
// Types
// ============================================

export interface OrganizedReport {
  /** Top 5 priority actions - sorted by impact/effort ratio */
  actionPlan: PrioritizedFinding[];
  /** High severity + quick effort = immediate wins */
  quickWins: PrioritizedFinding[];
  /** Issues visible without scrolling */
  aboveFold: AuditFinding[];
  /** Issues below the initial viewport */
  belowFold: AuditFinding[];
  /** Summary statistics */
  stats: ReportStats;
}

export interface PrioritizedFinding extends AuditFinding {
  /** Calculated priority score (higher = more important) */
  priorityScore: number;
  /** Human-readable priority reason */
  priorityReason: string;
}

export interface ReportStats {
  totalFindings: number;
  aboveFoldCount: number;
  belowFoldCount: number;
  quickWinCount: number;
  bySeverity: Record<SeverityLevel, number>;
  byEffort: Record<EffortEstimate, number>;
}

// ============================================
// Constants
// ============================================

/** Severity weight multipliers for priority scoring */
export const SEVERITY_WEIGHTS: Record<SeverityLevel, number> = {
  critical: 10,
  high: 7,
  medium: 4,
  low: 1,
};

/** Effort divisors for priority scoring (lower effort = higher priority) */
export const EFFORT_DIVISORS: Record<EffortEstimate, number> = {
  quick: 1,
  medium: 2,
  complex: 4,
};

/** Above-fold multiplier bonus */
export const ABOVE_FOLD_MULTIPLIER = 1.5;
// B-05: a finding on the path to the page's success action outranks one of the
// same severity elsewhere. Same weight as being above the fold, so the two
// commercial signals compound rather than one swamping the other.
export const ON_ACTION_PATH_MULTIPLIER = 1.5;

/** The priority score alone, for renderers that print it beside a finding. */
export function priorityScoreFor(finding: AuditFinding, viewportHeight: number = 1080): number {
  return calculatePriority(finding, viewportHeight).priorityScore;
}

// ============================================
// Main Organizer Function
// ============================================

/**
 * Organize findings into actionable report sections
 *
 * @param findings - Raw findings from AI analysis
 * @param viewportHeight - Viewport height for fold calculation (default: 1080 for desktop)
 * @returns Organized report with prioritized sections
 */
export function organizeFindings(
  findings: AuditFinding[],
  viewportHeight: number = 1080
): OrganizedReport {
  if (!findings || findings.length === 0) {
    return createEmptyReport();
  }

  // Step 1: Calculate priority scores for all findings
  const prioritizedFindings = findings.map((finding) =>
    calculatePriority(finding, viewportHeight)
  );

  // Step 2: Sort by priority score (descending)
  const sortedByPriority = [...prioritizedFindings].sort(
    (a, b) => b.priorityScore - a.priorityScore
  );

  // Step 3: Extract action plan (top 5)
  const actionPlan = sortedByPriority.slice(0, 5);

  // Step 4: Extract quick wins (high/critical severity + quick effort)
  const quickWins = prioritizedFindings.filter(
    (f) =>
      (f.severity === 'critical' || f.severity === 'high') &&
      f.effortEstimate === 'quick'
  );

  // Step 5: Segment by fold position
  const aboveFold = findings.filter((f) => isAboveFold(f, viewportHeight));
  const belowFold = findings.filter((f) => !isAboveFold(f, viewportHeight));

  // Step 6: Calculate statistics. (A per-device split used to be computed here
  // by exact principleId; nothing rendered it and compareDeviceFindings is the
  // one cross-device mechanism. Removed 2026-09-04, owner decision B-09.)
  const stats = calculateStats(findings, aboveFold, belowFold, quickWins);

  return {
    actionPlan,
    quickWins,
    aboveFold,
    belowFold,
    stats,
  };
}

// ============================================
// Priority Calculation
// ============================================

/**
 * Calculate priority score for a finding
 *
 * Formula: (severity_weight × above_fold_multiplier) / effort_divisor
 */
function calculatePriority(
  finding: AuditFinding,
  viewportHeight: number
): PrioritizedFinding {
  const severityWeight = SEVERITY_WEIGHTS[finding.severity] || SEVERITY_WEIGHTS.medium;
  const effortDivisor = EFFORT_DIVISORS[finding.effortEstimate || 'medium'] || EFFORT_DIVISORS.medium;
  const aboveFoldBonus = isAboveFold(finding, viewportHeight) ? ABOVE_FOLD_MULTIPLIER : 1;
  const onPathBonus = finding.onActionPath === true ? ON_ACTION_PATH_MULTIPLIER : 1;

  const priorityScore = (severityWeight * aboveFoldBonus * onPathBonus) / effortDivisor;

  // Generate human-readable reason
  const priorityReason = generatePriorityReason(finding, viewportHeight);

  return {
    ...finding,
    priorityScore,
    priorityReason,
  };
}

/**
 * Generate human-readable priority reason
 */
function generatePriorityReason(finding: AuditFinding, viewportHeight: number): string {
  const reasons: string[] = [];

  // Commercial reason first: it is the one the reader is paying for
  if (finding.onActionPath === true) {
    reasons.push('On the path to your success action');
  }

  // Severity reason
  if (finding.severity === 'critical') {
    reasons.push('Critical impact on users');
  } else if (finding.severity === 'high') {
    reasons.push('High impact issue');
  }

  // Position reason
  if (isAboveFold(finding, viewportHeight)) {
    reasons.push('Visible immediately');
  }

  // Effort reason
  if (finding.effortEstimate === 'quick') {
    reasons.push('Quick fix');
  }

  // Default reason if none
  if (reasons.length === 0) {
    reasons.push('Improves user experience');
  }

  return reasons.join(' • ');
}

// ============================================
// Helper Functions
// ============================================

/**
 * Determine if a finding is above the fold
 */
function isAboveFold(finding: AuditFinding, viewportHeight: number): boolean {
  // Check explicit evidence
  if (finding.evidence?.aboveFold !== undefined) {
    return finding.evidence.aboveFold;
  }

  // Check bounding box
  if (finding.evidence?.bbox) {
    return finding.evidence.bbox.y < viewportHeight;
  }

  // Default: assume above fold for critical/high severity
  // (these are likely more prominent issues)
  return finding.severity === 'critical' || finding.severity === 'high';
}

/**
 * Calculate report statistics
 */
function calculateStats(
  findings: AuditFinding[],
  aboveFold: AuditFinding[],
  belowFold: AuditFinding[],
  quickWins: PrioritizedFinding[]
): ReportStats {
  const bySeverity: Record<SeverityLevel, number> = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  const byEffort: Record<EffortEstimate, number> = {
    quick: 0,
    medium: 0,
    complex: 0,
  };

  findings.forEach((f) => {
    bySeverity[f.severity]++;
    byEffort[f.effortEstimate || 'medium']++;
  });

  return {
    totalFindings: findings.length,
    aboveFoldCount: aboveFold.length,
    belowFoldCount: belowFold.length,
    quickWinCount: quickWins.length,
    bySeverity,
    byEffort,
  };
}

/**
 * Create empty report structure
 */
function createEmptyReport(): OrganizedReport {
  return {
    actionPlan: [],
    quickWins: [],
    aboveFold: [],
    belowFold: [],
    stats: {
      totalFindings: 0,
      aboveFoldCount: 0,
      belowFoldCount: 0,
      quickWinCount: 0,
      bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
      byEffort: { quick: 0, medium: 0, complex: 0 },
    },
  };
}

// ============================================
// Export Additional Utilities
// ============================================

/**
 * Get effort label for display
 */
export function getEffortLabel(effort: EffortEstimate | undefined): string {
  const labels: Record<EffortEstimate, string> = {
    quick: '< 30 min',
    medium: '1-4 hours',
    complex: '> 4 hours',
  };
  return labels[effort || 'medium'];
}

/**
 * Get severity color for display
 */
export function getSeverityColor(severity: SeverityLevel): string {
  const colors: Record<SeverityLevel, string> = {
    critical: 'var(--critical)',
    high: 'var(--severity-high)',
    medium: 'var(--severity-medium)',
    low: 'var(--severity-low)',
  };
  return colors[severity];
}

/**
 * Format priority score for display
 */
export function formatPriorityScore(score: number): string {
  if (score >= 10) return 'Critical Priority';
  if (score >= 7) return 'High Priority';
  if (score >= 4) return 'Medium Priority';
  return 'Low Priority';
}
